import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { AdminSettingsService } from './admin-settings.service';
import { TenantViewModel, TenantFeesViewModel, UserViewModel } from '@program-suite/data-common';

interface AdminSettings {
  tenantDetails?: TenantViewModel | null;
  tenantFees?: TenantFeesViewModel | null;
  wholesalers?: UserViewModel[] | null;
}

const initialState: AdminSettings = {
  tenantDetails: null,
  tenantFees: null,
  wholesalers: null,
};

export const AdminSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<AdminSettings>(initialState),
  withComputed((state) => ({
    debug: computed(() => ({
      tenantDetails: state.tenantDetails,
      tenantFees: state.tenantFees,
      wholesalers: state.wholesalers,
    })),
  })),
  withMethods((store, adminSettingsService = inject(AdminSettingsService)) => ({
    setTenantDetails(tenantDetails: TenantViewModel) {
      patchState(store, {
        tenantDetails: tenantDetails,
      });
    },
    async getTenantDetails(tenantId?: number) {
      const tenantDetails = await adminSettingsService.getTenantDetails(tenantId);
      this.setTenantDetails(tenantDetails);
    },
    setTenantFees(tenantFees: TenantFeesViewModel | null) {
      patchState(store, {
        tenantFees: tenantFees,
      });
    },
    async getTenantFees(tenantId?: number) {
      const tenantFees = await adminSettingsService.getTenantFees(tenantId);
      this.setTenantFees(tenantFees);
    },
    setWholesalers(wholesalers: UserViewModel[]) {
      patchState(store, {
        wholesalers: wholesalers,
      });
    },
    async getWholesalers(tenantId?: number) {
      const wholesalers = await adminSettingsService.getWholesalers(tenantId);
      this.setWholesalers(wholesalers);
    },
    async updateTenantDetails(tenantDetails: TenantViewModel) {
      await adminSettingsService.updateTenantDetails(tenantDetails);
      this.setTenantDetails(tenantDetails);
    },
    async updateTenantFees(tenantFees: TenantFeesViewModel) {
      this.setTenantFees(null);
      await adminSettingsService.updateTenantFees(tenantFees);
      this.getTenantFees();
    },
    async resendInvite(userInviteGuid: string) {
      await adminSettingsService.resendInvite(userInviteGuid);
    },
    async cancelInvite(userInviteGuid: string) {
      await adminSettingsService.cancelInvite(userInviteGuid);
      this.getWholesalers();
    },
    async inviteUser(user: UserViewModel) {
      await adminSettingsService.inviteUser(user);
      this.getWholesalers();
    },
    async updateUser(user: UserViewModel) {
      await adminSettingsService.updateUser(user);
      this.getWholesalers();
    },
    reset() {
      patchState(store, initialState);
    },
  })),
);
